import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Card from '../../components/card/Card';
import Badge from '../../components/badge/Badge';
import { formatMoney } from '../../utils/Masks';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import Icon from '../../components/icon/Icon';
import IconButton from '../../components/icon-button/IconButton';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import IntegrateContributionModal from '../../components/modals/IntegrateContributionModal';
import WithdrawContributionModal from '../../components/modals/WithdrawContributionModal';
import { 
  getCapitalContributions, 
  getContributionAssetIntegrations,
  getContributionAssetIntegrationFormMetadata,
  checkProtectorCanContribute,
} from '../../services/GuaranteesService';
import Tooltip from '../../components/tooltip/Tooltip';
import { useConfig } from '../../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';
import { routes } from '../../routes/routePaths';

interface CapitalContribution {
  id: number;
  contribution_date: string;
  expiration_date: string;
  currency_name: string;
  currency_code?: string;
  amount: number;
  integrated_amount: number;
  authorized_fund_name: string;
  is_active: boolean;
  withdrawn_at?: string | null;
}

interface IntegratedContribution {
  id: number;
  contributionId?: number;
  contribution_id?: number;
  assetToIntegrateId?: number | null;
  asset_to_integrate_id?: number | null;
  nominalValue?: number | null;
  nominal_value?: number | null;
  price: number | null;
  currencyId?: number | null;
  currency_id?: number | null;
  currencyCode?: string | null;
  enabled: boolean;
  createdAt?: string | null;
  created_at?: string | null;
  updatedAt?: string | null;
  updated_at?: string | null;
}

interface AssetOption {
  id: number;
  name: string;
}

interface CurrencyOption {
  id: number;
  name: string;
}

interface FormMetadata {
  contributions: AssetOption[];
  assets_to_integrate: AssetOption[];
  currencies: CurrencyOption[];
}

interface ContributionSummary {
  total_ars: number;
  total_usd: number;
  total_converted_to_ars: number | null;
  total_converted_to_usd: number | null;
  quotation_value: number | null;
  quotation_date: string | null;
}

interface ProtectorDetailsContributionsSectionProps {
  protectorId?: string;
  onNewContribution?: () => void;
}

const TABLE_BORDER_COLOR = '#DFE6EB';

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex-shrink: 1;
`;

const ExpandToggle = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const IntegrateAction = styled.button<{ disabled?: boolean }>`
  padding: 0;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.45 : 1)};

  /* #00AB92 = success.bg.regular; el Icon usa 'svg path' (specificity 0,0,2),
     'i svg path' (0,0,3) lo sobreescribe sin necesidad de !important */
  i svg path {
    fill: ${(props) =>
      props.disabled
        ? theme.colors.neutral.bg.strong
        : theme.colors.success.bg.regular};
  }
`;

const InfoAction = styled.button<{ disabled?: boolean }>`
  padding: 0;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.45 : 1)};

  i svg path {
    fill: ${(props) =>
      props.disabled
        ? theme.colors.neutral.bg.strong
        : theme.colors.info.bg.regular};
  }
`;

const WithdrawAction = styled.button<{ disabled?: boolean }>`
  padding: 0;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.45 : 1)};

  i svg path {
    fill: ${(props) =>
      props.disabled
        ? theme.colors.neutral.bg.strong
        : theme.colors.danger.bg.regular};
  }
`;

const ViewWithdrawalAction = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  i svg path {
    fill: ${theme.colors.info.bg.regular};
  }
`;

const ActiveCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExpandedTableFrame = styled.div`
  background: ${theme.colors.neutral.bg.inverted};
  border: 1px solid ${TABLE_BORDER_COLOR};
  border-radius: ${theme.borderRadius['border-radius-2']};
  overflow: hidden;

  .rdt_TableCol:first-child,
  .rdt_TableCell:first-child {
    padding-left: ${theme.spacing['spacing-4']};
  }

  .rdt_TableCol:last-child,
  .rdt_TableCell:last-child {
    padding-right: ${theme.spacing['spacing-4']};
  }
`;

const ContributionsTableFrame = styled.div`
  /* Nine columns leave long headers below their text width: keep one line and scale the type down. */
  .rdt_TableCol {
    font-size: clamp(11px, 0.78vw, 13px);
  }

  .rdt_TableCol_Sortable {
    white-space: nowrap;
  }

  /* Acción is last: stretch the cell so the accordion chevron can sit at the far right.
     320px fits Retirar + Editar + Integrar + chevron on one line. */
  .rdt_TableCol:last-child,
  .rdt_TableCell:last-child {
    flex-grow: 2 !important;
    flex-basis: 0 !important;
    min-width: 320px !important;
  }

  .rdt_TableCell:last-child > * {
    width: 100%;
    max-width: 100%;
  }
`;


const NEW_CONTRIBUTION_DISABLED_TOOLTIP =
  'El protector debe tener al menos una acción como socio tipo B para poder aportar.';

const ProtectorDetailsContributionsSection = ({ 
  protectorId,
  onNewContribution
}: ProtectorDetailsContributionsSectionProps) => {
  const navigate = useNavigate();
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const localCurrencyName =
    localCurrencyCode === 'UYU' ? 'Pesos uruguayos' : 'Pesos';
  const [contributions, setContributions] = useState<CapitalContribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 0, size: 10, totalElements: 0, totalPages: 0 });
  const [isIntegrateModalOpen, setIsIntegrateModalOpen] = useState(false);
  const [selectedContributionId, setSelectedContributionId] = useState<number | null>(null);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [selectedWithdrawContributionId, setSelectedWithdrawContributionId] = useState<number | null>(null);
  const [withdrawModalReadOnly, setWithdrawModalReadOnly] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [integratedContributions, setIntegratedContributions] = useState<Map<number, IntegratedContribution[]>>(new Map());
  const [loadingIntegrations, setLoadingIntegrations] = useState<Set<number>>(new Set());
  const [formMetadata, setFormMetadata] = useState<FormMetadata | null>(null);
  const [summary, setSummary] = useState<ContributionSummary | null>(null);
  const [canCreateContribution, setCanCreateContribution] = useState(false);

  useEffect(() => {
    if (protectorId) {
      fetchContributions();
      fetchContributionEligibility();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protectorId, pagination.page]);

  useEffect(() => {
    loadFormMetadata();
  }, []);

  const fetchContributionEligibility = async () => {
    if (!protectorId) return;
    try {
      const canContribute = await checkProtectorCanContribute(protectorId);
      setCanCreateContribution(canContribute);
    } catch (err) {
      console.error(err);
      setCanCreateContribution(false);
    }
  };

  const loadFormMetadata = async () => {
    try {
      const response = await getContributionAssetIntegrationFormMetadata();
      if (response.success && 'data' in response) {
        setFormMetadata(response.data);
      }
    } catch (error) {
      console.error('Error loading form metadata:', error);
    }
  };

  const fetchContributions = async () => {
    if (!protectorId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getCapitalContributions(protectorId, pagination.page, pagination.size);
      if (response.success && 'data' in response) {
        setContributions(response.data.content || []);
        setPagination({
          page: response.data.page || 0,
          size: response.data.size || 10,
          totalElements: response.data.totalElements || 0,
          totalPages: response.data.totalPages || 0
        });
        if (response.data.summary) {
          setSummary(response.data.summary);
        }
      } else {
        setError('Error al cargar los aportes de capital');
      }
    } catch (err) {
      setError('Error al cargar los aportes de capital');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenIntegrateModal = (contributionId: number) => {
    setSelectedContributionId(contributionId);
    setIsIntegrateModalOpen(true);
  };

  const handleCloseIntegrateModal = () => {
    setIsIntegrateModalOpen(false);
    setSelectedContributionId(null);
  };

  const handleOpenWithdrawModal = (contributionId: number, readOnly = false) => {
    setSelectedWithdrawContributionId(contributionId);
    setWithdrawModalReadOnly(readOnly);
    setIsWithdrawModalOpen(true);
  };

  const handleCloseWithdrawModal = () => {
    setIsWithdrawModalOpen(false);
    setSelectedWithdrawContributionId(null);
    setWithdrawModalReadOnly(false);
  };

  const handleWithdrawalSuccess = () => {
    fetchContributions();
    if (selectedWithdrawContributionId) {
      const newMap = new Map(integratedContributions);
      newMap.delete(selectedWithdrawContributionId);
      setIntegratedContributions(newMap);
      // Collapse the detail panel after a successful withdrawal so the row starts collapsed.
      setExpandedRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(selectedWithdrawContributionId);
        return newSet;
      });
    }
  };

  const handleIntegrationSuccess = () => {
    fetchContributions();
    // Clear cached integrated contributions for the updated contribution
    if (selectedContributionId) {
      const newMap = new Map(integratedContributions);
      newMap.delete(selectedContributionId);
      setIntegratedContributions(newMap);
    }
  };

  const handleChangePage = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleToggleExpand = async (contributionId: number) => {
    const isExpanded = expandedRows.has(contributionId);
    
    if (isExpanded) {
      // Collapse: just remove from expanded rows
      setExpandedRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(contributionId);
        return newSet;
      });
    } else {
      // Expand: add to expanded rows immediately
      setExpandedRows(prev => new Set(prev).add(contributionId));
      
      // Check if we have cached data, if not, fetch it
      if (!integratedContributions.has(contributionId)) {
        setLoadingIntegrations(prev => new Set(prev).add(contributionId));
        try {
          const response = await getContributionAssetIntegrations(contributionId);
          console.log('Response from getContributionAssetIntegrations:', response);
          console.log('ContributionId:', contributionId);
          
          if (response.success && 'data' in response) {
            // The API returns an array directly
            const rawIntegrations = Array.isArray(response.data) 
              ? response.data 
              : [];
            console.log('Raw integrations from API:', rawIntegrations);
            
            // Normalize the data to handle both camelCase and snake_case
            const integrations: IntegratedContribution[] = rawIntegrations.map((item: any) => ({
              id: item.id,
              contributionId: item.contributionId || item.contribution_id,
              assetToIntegrateId: (item.assetToIntegrateId ?? item.asset_to_integrate_id) ?? null,
              nominalValue: (item.nominalValue ?? item.nominal_value) ?? null,
              price: item.price ?? null,
              currencyId: (item.currencyId ?? item.currency_id) ?? null,
              currencyCode: (item.currencyCode ?? item.currency_code) ?? null,
              enabled: item.enabled ?? true,
              createdAt: (item.createdAt ?? item.created_at) ?? null,
              updatedAt: (item.updatedAt ?? item.updated_at) ?? null,
            }));
            
            console.log('Normalized integrations:', integrations);
            console.log('Integrations length:', integrations.length);
            
            setIntegratedContributions(prev => {
              const newMap = new Map(prev);
              newMap.set(contributionId, integrations);
              return newMap;
            });
          } else {
            console.log('Response was not successful or missing data:', response);
          }
        } catch (error) {
          console.error('Error fetching integrated contributions:', error);
        } finally {
          setLoadingIntegrations(prev => {
            const newSet = new Set(prev);
            newSet.delete(contributionId);
            return newSet;
          });
        }
      }
    }
  };

  const handleRowClick = (row: CapitalContribution) => {
    if (row.withdrawn_at) {
      return;
    }
    handleToggleExpand(row.id);
  };

  const CURRENCY_CODE_MAP: Record<string, string> = {
    'PESO': 'ARS',
    'PESO ARGENTINO': 'ARS',
    'PESOS ARGENTINOS': 'ARS',
    'PESO URUGUAYO': 'UYU',
    'PESOS URUGUAYOS': 'UYU',
    'DOLAR': 'USD',
    'DÓLAR': 'USD',
    'DÓLAR ESTADOUNIDENSE': 'USD',
  };

  const mapCurrencyNameToCode = (name: string | null | undefined): string => {
    if (!name) return '-';
    return CURRENCY_CODE_MAP[name.toUpperCase()] ?? name;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amount: number) => {
    if (amount === null || amount === undefined) {
      return '0';
    }
    if (Number.isInteger(amount)) {
      return amount.toLocaleString('es-AR');
    }
    return amount.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getAssetName = (assetId: number | null): string => {
    if (!assetId || !formMetadata) return '-';
    const asset = formMetadata.assets_to_integrate?.find(a => a.id === assetId);
    return asset?.name || '-';
  };

  const getCurrencyName = (currencyId: number | null): string => {
    if (!currencyId || !formMetadata) return '-';
    const currency = formMetadata.currencies?.find(c => c.id === currencyId);
    return currency?.name || '-';
  };

  const calculateTotalIntegrated = (nominalValue: number | null, price: number | null): number => {
    if (!nominalValue || !price) return 0;
    return nominalValue * price;
  };

  const renderExpandedContent = (props: { data: CapitalContribution }) => {
    // react-data-table-component passes an object with 'data' property
    const row = props?.data || props as any;
    const contributionId = row?.id;
    const integrations = contributionId ? integratedContributions.get(contributionId) || [] : [];
    const isLoading = contributionId ? loadingIntegrations.has(contributionId) : false;

    console.log('renderExpandedContent - props:', props);
    console.log('renderExpandedContent - row:', row);
    console.log('renderExpandedContent - contributionId:', contributionId);
    console.log('renderExpandedContent - integrations:', integrations);
    console.log('renderExpandedContent - integrations.length:', integrations.length);
    console.log('renderExpandedContent - isLoading:', isLoading);
    console.log('renderExpandedContent - integratedContributions map:', Array.from(integratedContributions.entries()));

    if (isLoading) {
      return (
        <Card spacing="4">
          <P variant='neutral' modifier='weak' withMargin={false}>
            Cargando aportes integrados...
          </P>
        </Card>
      );
    }

    if (integrations.length === 0) {
      return (
        <Card spacing="4">
          <P variant='neutral' modifier='weak' withMargin={false}>
            No hay aportes integrados
          </P>
        </Card>
      );
    }

    const expandedColumns = [
      {
        name: <P variant='secondary-2' type="2" weight="medium" withMargin={false}>Fecha</P>,
        selector: (integration: IntegratedContribution) => (
          <P variant='secondary-2' type="2" weight="medium" withMargin={false}>
            {formatDate(integration.createdAt ?? '')}
          </P>
        ),
        minWidth: '140px',
        grow: 1
      },
      {
        name: <P variant='secondary-2' type="2" weight="medium" withMargin={false}>Moneda</P>,
        selector: (integration: IntegratedContribution) => (
          <P variant='secondary-2' type="2" weight="medium" withMargin={false} style={{ marginLeft: theme.spacing['spacing-2'] }}>
            {integration.currencyCode || mapCurrencyNameToCode(getCurrencyName(integration.currencyId ?? null))}
          </P>
        ),
        minWidth: '120px',
        grow: 1
      },
      {
        name: <P variant='secondary-2' type="2" weight="medium" withMargin={false}>Valor nominal</P>,
        selector: (integration: IntegratedContribution) => (
          <P variant='secondary-2' type="2" weight="medium" withMargin={false} style={{ marginLeft: theme.spacing['spacing-2'] }}>
            {formatAmount(integration.nominalValue || 0)}
          </P>
        ),
        minWidth: '140px',
        grow: 1
      },
      {
        name: <P variant='secondary-2' type="2" weight="medium" withMargin={false}>Precio</P>,
        selector: (integration: IntegratedContribution) => (
          <P variant='secondary-2' type="2" weight="medium" withMargin={false}>
            {formatMoney(integration.price)}
          </P>
        ),
        minWidth: '140px',
        grow: 1
      },
      {
        name: <P variant='secondary-2' type="2" weight="medium" withMargin={false}>Activo</P>,
        selector: (integration: IntegratedContribution) => (
          <P variant='secondary-2' type="2" weight="medium" withMargin={false}>
            {getAssetName(integration.assetToIntegrateId ?? null)}
          </P>
        ),
        minWidth: '150px',
        grow: 1.5
      },
      {
        name: <P variant='secondary-2' type="2" weight="medium" withMargin={false}>Total integrado</P>,
        selector: (integration: IntegratedContribution) => (
          <P variant='secondary-2' type="2" weight="medium" withMargin={false}>
            {formatMoney(calculateTotalIntegrated(integration.nominalValue ?? null, integration.price ?? null))}
          </P>
        ),
        minWidth: '160px',
        grow: 1
      }
    ];

    return (
      <ExpandedTableFrame>
        <Table
          columns={expandedColumns}
          data={integrations}
          tableData={{ page: 0, size: integrations.length, totalElements: integrations.length, totalPages: 1 }}
          disablePagination={true}
          variant='weak'
          customStyles={{
            responsiveWrapper: {
              style: {
                borderTop: 'none',
                borderBottom: 'none',
                borderRadius: 0,
                overflowX: 'auto',
              },
            },
            headRow: {
              style: {
                borderBottom: `1px solid ${TABLE_BORDER_COLOR}`,
              },
            },
            headCells: {
              style: {
                justifyContent: 'flex-start',
                textAlign: 'left',
              },
            },
            rows: {
              style: {
                borderBottom: 'none',
              },
            },
            cells: {
              style: {
                justifyContent: 'flex-start',
                textAlign: 'left',
              },
            },
          }}
        />
      </ExpandedTableFrame>
    );
  };

  const columns = [
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>N° de aporte</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {row.id ?? '-'}
        </P>
      ),
      minWidth: '110px',
      grow: 0.8
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. de aporte</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {formatDate(row.contribution_date)}
        </P>
      ),
      minWidth: '135px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. de retiro</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {formatDate(row.withdrawn_at ?? '')}
        </P>
      ),
      minWidth: '135px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. de vencimiento</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {formatDate(row.expiration_date)}
        </P>
      ),
      minWidth: '165px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Moneda</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {row.currency_code || mapCurrencyNameToCode(row.currency_name)}
        </P>
      ),
      minWidth: '80px',
      grow: 0.6
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Importe aportado</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {formatMoney(row.amount)}
        </P>
      ),
      minWidth: '145px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Importe integrado</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {formatMoney(row.integrated_amount || 0)}
        </P>
      ),
      minWidth: '150px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fondo autorizado</P>,
      selector: (row: CapitalContribution) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {row.authorized_fund_name || '-'}
        </P>
      ),
      minWidth: '150px',
      grow: 1.5
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Esta activo</P>,
      selector: (row: CapitalContribution) => {
        const isWithdrawn = Boolean(row.withdrawn_at);
        const isActive = row.is_active && !isWithdrawn;
        return (
          <ActiveCell>
            <P
              variant='secondary-2'
              type="3"
              weight='regular'
              withMargin={false}
            >
              {isActive ? 'Si' : 'No'}
            </P>
            {isWithdrawn && (
              <Badge type="text" variant="danger" size="sm" weight="bold">
                RETIRADO
              </Badge>
            )}
          </ActiveCell>
        );
      },
      minWidth: '140px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Acción</P>,
      selector: (row: CapitalContribution) => {
        const isWithdrawn = Boolean(row.withdrawn_at);
        const isEditable = row.is_active && !isWithdrawn;
        return (
          <ActionCell data-testid={`row-actions-${row.id}`}>
            <ActionGroup>
              {isWithdrawn && (
                <ViewWithdrawalAction
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenWithdrawModal(row.id, true);
                  }}
                >
                  <Icon
                    name='view_outline'
                    variant='info'
                    modifier='regular'
                    width={20}
                    height={20}
                  />
                  <P
                    type='4'
                    weight='bold'
                    withMargin={false}
                    style={{ color: theme.colors.info.bg.regular }}
                  >
                    Ver retiro
                  </P>
                </ViewWithdrawalAction>
              )}
              {isEditable && (
                <>
                  <WithdrawAction
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenWithdrawModal(row.id);
                    }}
                  >
                    <Icon
                      name='arrow_uturn_left_outline'
                      variant='danger'
                      modifier='regular'
                      width={20}
                      height={20}
                    />
                    <P
                      type='4'
                      weight='bold'
                      withMargin={false}
                      style={{ color: theme.colors.danger.bg.regular }}
                    >
                      Retirar
                    </P>
                  </WithdrawAction>
                  <InfoAction
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        routes.protectorContributionEdit
                          .replace(':id', String(protectorId))
                          .replace(':contributionId', String(row.id)),
                      );
                    }}
                  >
                    <Icon
                      name='edit_outline'
                      variant='info'
                      modifier='regular'
                      width={20}
                      height={20}
                    />
                    <P
                      type='4'
                      weight='bold'
                      withMargin={false}
                      style={{ color: theme.colors.info.bg.regular }}
                    >
                      Editar
                    </P>
                  </InfoAction>
                  <IntegrateAction
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenIntegrateModal(row.id);
                    }}
                  >
                    <Icon
                      name='plus_icon'
                      variant='success'
                      modifier='regular'
                      width={20}
                      height={20}
                    />
                    <P
                      type='4'
                      weight='bold'
                      withMargin={false}
                      style={{ color: theme.colors.success.bg.regular }}
                    >
                      Integrar
                    </P>
                  </IntegrateAction>
                </>
              )}
            </ActionGroup>
            <ExpandToggle>
              <IconButton
                name={expandedRows.has(row.id) ? 'chevron_down_outline' : 'chevron_right_outline'}
                ariaLabel={
                  expandedRows.has(row.id) ? 'Contraer detalle' : 'Expandir detalle'
                }
                variant='info'
                modifier='regular'
                size={expandedRows.has(row.id) ? 24 : 20}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand(row.id);
                }}
              />
            </ExpandToggle>
          </ActionCell>
        );
      },
      minWidth: '320px',
      grow: 3,
      style: {
        minWidth: '320px',
      },
    }
  ];

  return (
    <>
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
        <H withMargin={false} type='2' weight='bold' modifier='strong'>
          Aportes de capital
        </H>
        {onNewContribution && (
          canCreateContribution ? (
            <Button variant='secondary' icon='plus_icon' iconAlign='left' onClick={onNewContribution}>
              Nuevo aporte
            </Button>
          ) : (
            <div
              style={{
                display: 'inline-flex',
                flex: '0 0 auto',
                width: 'auto',
                alignSelf: 'center',
              }}
            >
              <Tooltip position='bottom' msg={NEW_CONTRIBUTION_DISABLED_TOOLTIP}>
                <span style={{ display: 'inline-flex' }}>
                  <Button variant='secondary' icon='plus_icon' iconAlign='left' disabled>
                    Nuevo aporte
                  </Button>
                </span>
              </Tooltip>
            </div>
          )
        )}
      </Card>

      {summary && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Card variant='neutral' modifier='regular' style={{ flex: '1 1 200px', padding: '16px' }}>
              <H withMargin={false} type='3' weight='semibold' modifier='strong'>
                {localCurrencyCode} {formatAmount(summary.total_ars)}
              </H>
              <P variant='neutral' modifier='strong' type="1" withMargin={false}>
                {localCurrencyName} aportados
              </P>
            </Card>
            <Card variant='neutral' modifier='regular' style={{ flex: '1 1 200px', padding: '16px' }}>
              <H withMargin={false} type='3' weight='semibold' modifier='strong'>
                {summary.total_converted_to_ars !== null
                  ? `${localCurrencyCode} ${formatAmount(summary.total_converted_to_ars)}`
                  : '—'}
              </H>
              <P variant='neutral' modifier='strong' type="1" withMargin={false}>
                Equivalente en {localCurrencyName.toLowerCase()}
              </P>
            </Card>
            <Card variant='neutral' modifier='regular' style={{ flex: '1 1 200px', padding: '16px' }}>
              <H withMargin={false} type='3' weight='semibold' modifier='strong'>
                USD {formatAmount(summary.total_usd)}
              </H>
              <P variant='neutral' modifier='strong' type="1" withMargin={false}>
                Dólares aportados
              </P>
            </Card>
            <Card variant='neutral' modifier='regular' style={{ flex: '1 1 200px', padding: '16px' }}>
              <H withMargin={false} type='3' weight='semibold' modifier='strong'>
                {summary.total_converted_to_usd !== null
                  ? `USD ${formatAmount(summary.total_converted_to_usd)}`
                  : '—'}
              </H>
              <P variant='neutral' modifier='strong' type="1" withMargin={false}>
                Equivalente en dólares
              </P>
            </Card>
          </div>
          {summary.quotation_value !== null && (
            <P variant='neutral' modifier='weak' type="3" withMargin={false} style={{ marginTop: '8px', textAlign: 'right' }}>
              Cotización USD: $ {formatAmount(summary.quotation_value)}
            </P>
          )}
        </div>
      )}
      
      {loading ? (
        <Card>
          <TableSkeleton rows={5} columns={8} />
        </Card>
      ) : error ? (
        <Card>
          <P variant='neutral' modifier='weak' withMargin={false}>
            {error}
          </P>
        </Card>
      ) : contributions && contributions.length > 0 ? (
        <Card>
          <ContributionsTableFrame>
            <Table
              columns={columns}
              data={contributions}
              tableData={pagination}
              handleChangePage={handleChangePage}
              onRowClicked={handleRowClick}
              expandableRows={true}
              expandableRowsComponent={(props: { data: CapitalContribution }) => renderExpandedContent(props)}
              expandableRowExpanded={(row: CapitalContribution) => expandedRows.has(row.id)}
              conditionalRowStyles={[
                {
                  when: (row: CapitalContribution) => expandedRows.has(row.id),
                  style: { backgroundColor: TABLE_BORDER_COLOR },
                },
              ]}
              customStyles={{
                expanderCell: { style: { display: 'none', minWidth: '0', width: '0', padding: '0' } },
                expanderButton: { style: { display: 'none' } },
              }}
            />
          </ContributionsTableFrame>
        </Card>
      ) : (
        <Card>
          <P variant='neutral' modifier='weak' withMargin={false}>
            No hay aportes de capital para mostrar.
          </P>
        </Card>
      )}

      {selectedContributionId && (
        <IntegrateContributionModal
          isOpen={isIntegrateModalOpen}
          onClose={handleCloseIntegrateModal}
          contributionId={selectedContributionId}
          amount={contributions.find(c => c.id === selectedContributionId)?.amount || 0}
          integrated_amount={contributions.find(c => c.id === selectedContributionId)?.integrated_amount || 0}
          currency_name={contributions.find(c => c.id === selectedContributionId)?.currency_name || ''}
          onSuccess={handleIntegrationSuccess}
        />
      )}

      {selectedWithdrawContributionId && (
        <WithdrawContributionModal
          isOpen={isWithdrawModalOpen}
          onClose={handleCloseWithdrawModal}
          contributionId={selectedWithdrawContributionId}
          contributionDate={
            contributions.find((c) => c.id === selectedWithdrawContributionId)?.contribution_date
          }
          fundName={
            contributions.find((c) => c.id === selectedWithdrawContributionId)?.authorized_fund_name
          }
          protectorId={protectorId}
          readOnly={withdrawModalReadOnly}
          onSuccess={handleWithdrawalSuccess}
        />
      )}
    </>
  );
};

export default ProtectorDetailsContributionsSection;

